import { useCallback, useEffect, useId, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { renderGuestQrDataUrl } from './guestQrCode';
import './ReportPdfQrPanel.css';

/**
 * Download-report CTA → modal with QR for phone PDF download.
 * Generates the download URL only when the CTA is opened (not on page load).
 * Modal is portaled to document.body so PDF DOM capture (which hides
 * [data-pdf-exclude] inside the report) cannot hide the QR while preparing.
 */
export default function ReportPdfQrPanel({
  createDownloadUrl,
  enabled = true,
  ctaLabel = 'Download report',
  title = 'Scan to download PDF',
  subtitle = 'Scan with your phone to save your report as a PDF.',
  panelTitle = 'Save your report',
  panelSubtitle = 'Tap download to show a QR code you can scan on your phone.',
  className = '',
}) {
  const titleId = useId();
  const closeRef = useRef(null);
  const createDownloadUrlRef = useRef(createDownloadUrl);
  const [open, setOpen] = useState(false);
  const [qrDataUrl, setQrDataUrl] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  createDownloadUrlRef.current = createDownloadUrl;

  const closeModal = useCallback(() => {
    setOpen(false);
  }, []);

  const openModal = useCallback(() => {
    if (!enabled) return;
    setOpen(true);
  }, [enabled]);

  useEffect(() => {
    if (!open) return undefined;
    if (!enabled || typeof createDownloadUrlRef.current !== 'function') {
      setQrDataUrl('');
      setError('Report is still generating');
      return undefined;
    }

    let cancelled = false;
    (async () => {
      setBusy(true);
      setError('');
      setQrDataUrl('');
      try {
        const url = await createDownloadUrlRef.current();
        if (cancelled) return;
        if (!url) throw new Error('PDF download link unavailable');
        const dataUrl = await renderGuestQrDataUrl(url, 280);
        if (!cancelled) setQrDataUrl(dataUrl);
      } catch (e) {
        if (!cancelled) {
          setQrDataUrl('');
          setError(e?.message || 'Could not prepare PDF QR');
        }
      } finally {
        if (!cancelled) setBusy(false);
      }
    })();

    return () => {
      cancelled = true;
    };
    // Intentionally depend only on open/enabled — createDownloadUrl is read via ref
    // so parent re-renders during PDF capture cannot cancel QR generation.
  }, [open, enabled]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') closeModal();
    };
    document.addEventListener('keydown', onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    closeRef.current?.focus?.();
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [open, closeModal]);

  const modal = open ? (
    <div className="report-pdf-qr-modal" role="presentation" onClick={closeModal}>
      <div
        className="report-pdf-qr-modal__card"
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        onClick={(e) => e.stopPropagation()}
      >
        <button
          ref={closeRef}
          type="button"
          className="report-pdf-qr-modal__close"
          onClick={closeModal}
          aria-label="Close"
        >
          ×
        </button>
        <h3 id={titleId} className="report-pdf-qr-modal__title">
          {title}
        </h3>
        <p className="report-pdf-qr-modal__sub">{subtitle}</p>
        <div className="report-pdf-qr-modal__frame">
          {busy ? (
            <div className="report-pdf-qr-loading" aria-busy="true">
              Preparing…
            </div>
          ) : qrDataUrl ? (
            <img
              src={qrDataUrl}
              alt="QR code to download PDF report"
              className="report-pdf-qr-image"
            />
          ) : (
            <div className="report-pdf-qr-empty">QR unavailable</div>
          )}
        </div>
        {error ? <p className="report-pdf-qr-error">{error}</p> : null}
      </div>
    </div>
  ) : null;

  return (
    <div
      className={`report-pdf-qr-panel report-pdf-qr-panel--cta ${className}`.trim()}
      data-pdf-exclude="true"
    >
      <div className="report-pdf-qr-copy">
        <h2 className="report-pdf-qr-title">{panelTitle}</h2>
        <p className="report-pdf-qr-sub">{panelSubtitle}</p>
      </div>
      <button
        type="button"
        className="report-pdf-qr-cta"
        onClick={openModal}
        disabled={!enabled}
      >
        {ctaLabel}
      </button>

      {typeof document !== 'undefined' && modal
        ? createPortal(modal, document.body)
        : null}
    </div>
  );
}
